import {
  Avatar,
  Box,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { ReactNode, useState } from 'react';
import { visitApi } from 'src/api/visit';
import FilesUploadModal, {
  FilesUploadModalFormValuesProps,
} from 'src/components/files-upload-modal';
import Image from 'src/components/image';
import Scrollbar from 'src/components/scrollbar';
import { TablePaginationCustom, useTable } from 'src/components/table';
import VisitNotesStatusBadge, {
  resolveVisitNotesStatus,
  VisitNotesStatus,
} from 'src/components/visit-status-badge/VisitNotesStatusBadge';
import VisitStatusBadge from 'src/components/visit-status-badge/VisitStatusBadge';
import OrganizationVisitModal from 'src/sections/@dashboard/organization/components/OrganizationVisitModal/OrganizationVisitModal';
import hasRichTextContent from 'src/utils/hasRichTextContent';
import { formatVisitClosure } from 'src/utils/visitClosure';
import {
  GetProfessionalConfigDtoEnabledCategoriesEnum,
  GetVisitById,
  GetVisitByIdRejectedByEnum,
  GetVisitByIdStatusEnum,
  VisitApiVisitControllerAddDocumentsRequest,
} from 'woofwoof-api';
import { translateKey } from 'src/locales/translate';


interface Row {
  id: string;
  profile?: string;
  profileAvatar?: string;
  date: string;
  closedAt?: string | null;
  endTime?: string;
  user?: string;
  userAvatar?: string;
  category: GetProfessionalConfigDtoEnabledCategoriesEnum;
  actions: string;
  status: GetVisitByIdStatusEnum;
  rejectedBy?: GetVisitByIdRejectedByEnum;
  notesStatus: VisitNotesStatus | null;
}

interface Column {
  id: keyof Row;
  label: string;
  minWidth?: number;
  align?: 'left' | 'center';
  render?: (row: Row) => ReactNode;
}

export type VisitsHistoryTableProps = {
  data: GetVisitById[];
  total?: number;
  page?: number;
  rowsPerPage?: number;
  onChangePage?: (newPage: number) => void;
  onChangeRowsPerPage?: (newRowsPerPage: number) => void;
  isFetching?: boolean;
  refetch: () => void;
};

export default function OrganizationProfessionalVisitsHistoryTable({
  data,
  total,
  page: externalPage,
  rowsPerPage: externalRowsPerPage,
  onChangePage: externalOnChangePage,
  onChangeRowsPerPage: externalOnChangeRowsPerPage,
  isFetching = false,
  refetch,
}: VisitsHistoryTableProps) {
  const theme = useTheme();

  const { enqueueSnackbar } = useSnackbar();
  const [selectedVisitId, setSelectedVisitId] = useState('');
  const [selectedVisitIdForUpload, setSelectedVisitIdForUpload] = useState('');

  const openModal = (visitId: string) => setSelectedVisitId(visitId);

  const closeModal = () => setSelectedVisitId('');

  const handleOnRowClick = (visitId: string) => {
    openModal(visitId);
  };

  const { mutateAsync, isLoading: isUploadLoading } = useMutation({
    mutationFn: (
      visitAddDocumentsDto: VisitApiVisitControllerAddDocumentsRequest,
    ) => visitApi.visitControllerAddDocuments(visitAddDocumentsDto),
  });

  const openUploadModal = (visitId: string) =>
    setSelectedVisitIdForUpload(visitId);

  const closeUploadModal = () => setSelectedVisitIdForUpload('');

  const handleOnUpload = async (
    files: FilesUploadModalFormValuesProps['files'],
  ) => {
    if (files) {
      try {
        await mutateAsync({
          visitId: selectedVisitIdForUpload,
          documentFileDto: files,
        });
        closeUploadModal();
        refetch();
      } catch (error) {
        enqueueSnackbar(error.message || error, {
          variant: 'error',
        });
      }
    }
  };

  const handleOnUploadClick = (visitId: string) => openUploadModal(visitId);

  const tableData: Row[] =
    //TODO: Add proper type
    data?.map((visit) => ({
      id: visit.id,
      profile: visit?.profile?.name,
      profileAvatar: visit?.profile?.avatar,
      user: `${visit?.user?.firstName}  ${visit?.user?.lastName}`,
      userAvatar: visit?.user?.avatar,
      date: visit.startTime,
      closedAt: visit.closedAt,
      endTime: visit.endTime,
      category: visit.category,
      status: visit.status,
      rejectedBy: visit.rejectedBy,
      actions: '',
      notesStatus: resolveVisitNotesStatus({
        status: visit.status,
        hasNotes: hasRichTextContent(visit.notes),
        hasNotesDraft: visit.hasNotesDraft,
      }),
    })) || [];

  const COLUMNS: Column[] = [
    {
      id: 'profile',
      // Staff-only tab, like the clinic-wide history it mirrors.
      label: translateKey("visit.tablePatient"),
      render: (value: Row) => (
        <Box
          sx={{
            gap: theme.spacing(2),
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Avatar
            src={value.profileAvatar || '/assets/avatars/avatar4.png'}
            sx={{ width: theme.spacing(5), height: theme.spacing(5) }}
          />
          <Typography variant="body2">{value.profile}</Typography>
        </Box>
      ),
    },
    {
      id: 'user',
      label: translateKey("common.owner"),
      render: (value: Row) => (
        <Box
          sx={{
            gap: theme.spacing(2),
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Avatar
            src={value.userAvatar || '/assets/avatars/avatar.png'}
            sx={{ width: theme.spacing(5), height: theme.spacing(5) }}
          />
          <Typography variant="body2">{value.user}</Typography>
        </Box>
      ),
    },
    {
      id: 'date',
      label: translateKey("visit.tableDate"),
      render: (value: Row) => format(new Date(value.date), 'dd/MM/yyyy H:mm'),
    },
    {
      id: 'closedAt',
      label: translateKey('visit.historyClosedColumn'),
      render: (value: Row) => formatVisitClosure(value),
    },
    {
      id: 'notesStatus',
      label: translateKey("visit.notesStatusColumn"),
      render: (value: Row) =>
        value.notesStatus ? (
          <VisitNotesStatusBadge status={value.notesStatus} />
        ) : null,
    },
    {
      id: 'status',
      label: translateKey("visit.tableStatus"),
      render: (value: Row) => (
        <VisitStatusBadge
          visitStatus={value.status}
          rejectedBy={value.rejectedBy}
        />
      ),
    },
  ];
  // Use external pagination if provided, otherwise use local state
  const useLocalPagination = externalPage === undefined;
  const localTable = useTable({
    defaultRowsPerPage: 25,
  });

  const page = useLocalPagination ? localTable.page : externalPage!;
  const rowsPerPage = useLocalPagination
    ? localTable.rowsPerPage
    : externalRowsPerPage!;

  const onChangePage = useLocalPagination
    ? localTable.onChangePage
    : (event: unknown, newPage: number) => {
        externalOnChangePage!(newPage);
      };

  const onChangeRowsPerPage = useLocalPagination
    ? localTable.onChangeRowsPerPage
    : (event: React.ChangeEvent<HTMLInputElement>) => {
        externalOnChangeRowsPerPage!(parseInt(event.target.value, 10));
      };

  const TableHeader = (
    <Table stickyHeader>
      <TableHead>
        <TableRow>
          {COLUMNS.map((column) => (
            <TableCell
              key={column.id}
              align={column.align}
              style={{ minWidth: column.minWidth }}
            >
              {column.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    </Table>
  );

  const FullTable = (
    <Box sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}>
      <Scrollbar>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching
              ? Array.from({ length: rowsPerPage }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    <TableCell align="left">
                      <Box
                        sx={{
                          gap: theme.spacing(2),
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Skeleton
                          variant="circular"
                          height="40px"
                          width="40px"
                        />
                        <Skeleton
                          variant="rounded"
                          height="22px"
                          width="140px"
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="left">
                      <Box
                        sx={{
                          gap: theme.spacing(2),
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Skeleton
                          variant="circular"
                          height="40px"
                          width="40px"
                        />
                        <Skeleton
                          variant="rounded"
                          height="22px"
                          width="140px"
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="left">
                      <Skeleton variant="rounded" height="24px" width="120px" />
                    </TableCell>
                    <TableCell align="left">
                      <Skeleton variant="rounded" height="24px" width="134px" />
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        component="span"
                        display="flex"
                        flex={1}
                        justifyContent="center"
                      >
                        <Skeleton
                          variant="circular"
                          height="40px"
                          width="40px"
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              : (useLocalPagination
                  ? tableData.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage,
                    )
                  : tableData
                ).map((row, rowIndex) => (
                  <TableRow
                    hover
                    key={rowIndex}
                    data-testid="visit-history-row"
                    data-visit-id={row.id}
                    onClick={() => handleOnRowClick(row.id)}
                  >
                    {COLUMNS.map((column, index) => {
                      const value = row[column.id];
                      return (
                        <TableCell key={column.id} align={column.align}>
                          {column.render ? column.render(row) : value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </Scrollbar>
    </Box>
  );

  return (
    <>
      <TableContainer
        sx={{ height: 1, display: 'flex', flexDirection: 'column' }}
      >
        {tableData.length > 0 ? (
          FullTable
        ) : (
          <>
            {TableHeader}
            <Stack
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              flex="1"
              gap={2}
            >
              <Image
                sx={{ width: '200px' }}
                alt="empty waiting room"
                src="/assets/images/empty_table.svg"
              />
              <Typography>{translateKey("visit.upcomingNoVisits")}</Typography>
            </Stack>
          </>
        )}
      </TableContainer>

      <TablePaginationCustom
        count={useLocalPagination ? tableData.length : total || 0}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
      />
      <OrganizationVisitModal
        isOpen={Boolean(selectedVisitId)}
        onClose={closeModal}
        visit={data?.find((visit) => visit.id === selectedVisitId)}
      />
      <FilesUploadModal
        isOpen={Boolean(selectedVisitIdForUpload)}
        onClose={closeUploadModal}
        onUpload={handleOnUpload}
        isLoading={isUploadLoading}
      />
    </>
  );
}
